import { AlertTriangle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "browser-compatibility-dismissed";
const DISMISS_DURATION_DAYS = 30;

type SupportedBrowser = "chrome" | "firefox" | "safari" | "edge";

// Minimum major versions for supported browsers (baseline: 2024 releases).
const MIN_SUPPORTED_VERSIONS: Record<SupportedBrowser, number> = {
	chrome: 120,
	firefox: 120,
	safari: 17,
	edge: 120,
};

type BrowserInfo = {
	name: SupportedBrowser | "other";
	versionMajor: number | null;
};

function extractMajorVersion(versionString: string | null): number | null {
	if (!versionString) return null;
	const match = versionString.match(/^(\d+)/);
	if (!match) return null;
	const value = Number.parseInt(match[1], 10);
	return Number.isNaN(value) ? null : value;
}

function getBrowserInfoFromUserAgent(userAgent: string): BrowserInfo {
	const ua = userAgent.toLowerCase();

	// Microsoft Edge (Chromium)
	if (ua.includes("edg/")) {
		const match = ua.match(/edg\/(\d+[.\d+]*)/);
		return {
			name: "edge",
			versionMajor: extractMajorVersion(match?.[1] ?? null),
		};
	}

	// Firefox
	if (ua.includes("firefox/")) {
		const match = ua.match(/firefox\/(\d+[.\d+]*)/);
		return {
			name: "firefox",
			versionMajor: extractMajorVersion(match?.[1] ?? null),
		};
	}

	// Chrome (exclude Edge, Opera, Chromium derivatives explicitly)
	if (
		ua.includes("chrome/") &&
		!ua.includes("edg/") &&
		!ua.includes("opr/") &&
		!ua.includes("opera") &&
		!ua.includes("chromium")
	) {
		const match = ua.match(/chrome\/(\d+[.\d+]*)/);
		return {
			name: "chrome",
			versionMajor: extractMajorVersion(match?.[1] ?? null),
		};
	}

	// Safari (including iOS Safari) – look for Version/x.y and Safari but not Chrome
	if (
		ua.includes("safari") &&
		ua.includes("version/") &&
		!ua.includes("chrome") &&
		!ua.includes("chromium")
	) {
		const match = ua.match(/version\/(\d+[.\d+]*)/);
		return {
			name: "safari",
			versionMajor: extractMajorVersion(match?.[1] ?? null),
		};
	}

	return { name: "other", versionMajor: null };
}

/**
 * Checks if the current browser is one of the supported ones and meets the
 * minimum version requirement. Only Safari, Chrome, Firefox and Edge are
 * considered supported; everything else is treated as unsupported.
 */
function isBrowserVersionSupported(): boolean {
	try {
		if (
			typeof navigator === "undefined" ||
			typeof navigator.userAgent !== "string"
		) {
			// In non-browser environments (SSR, tests without a UA), do not block based
			// on browser version. Feature detection still provides protection.
			return true;
		}

		const info = getBrowserInfoFromUserAgent(navigator.userAgent);
		if (info.name === "other") {
			// Not one of the explicitly supported browsers.
			return false;
		}

		if (info.versionMajor === null) {
			// Unknown version for a supported browser – assume supported to avoid
			// false positives.
			return true;
		}

		const minimum = MIN_SUPPORTED_VERSIONS[info.name];
		return info.versionMajor >= minimum;
	} catch {
		// If user agent parsing fails, do not penalize the user; rely on
		// feature detection instead.
		return true;
	}
}

/**
 * Checks if the browser supports critical features required by the application.
 * Uses feature detection instead of user agent sniffing for accuracy.
 */
function checkBrowserCompatibility(): boolean {
	try {
		// Check for essential ES6+ features
		if (typeof Promise === "undefined") return false;
		if (typeof Symbol === "undefined") return false;
		if (typeof Map === "undefined") return false;
		if (typeof Set === "undefined") return false;

		// Check for Fetch API
		if (typeof fetch === "undefined") return false;

		// Check for modern Array methods
		if (typeof Array.prototype.includes === "undefined") return false;
		if (typeof Array.prototype.find === "undefined") return false;

		// Check for Object methods
		if (typeof Object.assign === "undefined") return false;
		if (typeof Object.entries === "undefined") return false;

		// Check for IntersectionObserver (used for lazy loading)
		if (typeof IntersectionObserver === "undefined") return false;

		// Check for ResizeObserver (used for responsive components)
		if (typeof ResizeObserver === "undefined") return false;

		// Check for localStorage (required for state persistence)
		if (typeof localStorage === "undefined") return false;

		// Check for CSS Grid support
		if (!CSS.supports("display", "grid")) return false;

		// Check for CSS Flexbox support
		if (!CSS.supports("display", "flex")) return false;

		// Check for CSS custom properties (variables)
		if (!CSS.supports("--test", "0")) return false;

		return true;
	} catch {
		// If any check throws an error, the browser is likely too old
		return false;
	}
}

/**
 * Checks if the user has previously dismissed the warning within the expiration period.
 */
function isDismissed(): boolean {
	try {
		const dismissed = localStorage.getItem(STORAGE_KEY);
		if (!dismissed) return false;

		const dismissedDate = new Date(dismissed);
		const now = new Date();
		const diffDays = Math.floor(
			(now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24),
		);

		return diffDays < DISMISS_DURATION_DAYS;
	} catch {
		return false;
	}
}

/**
 * Saves the dismissal timestamp to localStorage.
 */
function saveDismissal(): void {
	try {
		localStorage.setItem(STORAGE_KEY, new Date().toISOString());
	} catch {
		// Silently fail if localStorage is not available
	}
}

/**
 * A banner component that warns users when their browser may not be fully compatible
 * with the application. Uses feature detection to determine compatibility and allows
 * users to dismiss the warning for 30 days.
 */
export function BrowserCompatibilityBanner() {
	const { t } = useTranslation();
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		// First check if the user has already dismissed the banner recently.
		// If so, skip further compatibility checks to avoid unnecessary work.
		const wasDismissed = isDismissed();
		if (wasDismissed) {
			return;
		}

		// Only show if browser is incompatible (by features or version support).
		const isCompatibleByFeatures = checkBrowserCompatibility();
		const isSupportedByBrowser = isBrowserVersionSupported();

		if (!isCompatibleByFeatures || !isSupportedByBrowser) {
			setIsVisible(true);
		}
	}, []);

	const handleDismiss = () => {
		saveDismissal();
		setIsVisible(false);
	};

	if (!isVisible) {
		return null;
	}

	return (
		<div
			className="fixed top-0 left-0 right-0 z-9999 bg-warning text-warning-content"
			role="alert"
			aria-live="polite"
		>
			<div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
				<div className="flex items-center gap-3">
					<AlertTriangle className="h-5 w-5 shrink-0" />
					<div className="text-sm">
						<span className="font-medium">
							{t("browserCompatibility.title")}
						</span>
						<span className="ml-1">{t("browserCompatibility.message")}</span>
					</div>
				</div>
				<button
					type="button"
					onClick={handleDismiss}
					className="shrink-0 p-1 rounded-full hover:bg-warning-content/20 transition-colors"
					aria-label={t("common.dismiss")}
				>
					<X className="h-5 w-5" />
				</button>
			</div>
		</div>
	);
}

export default BrowserCompatibilityBanner;
